import React, { useEffect, useMemo, useState } from "react";
import Modal from "../components/Modal";
import {
	getFinalExam,
	listFinalExams,
	resetFinalExam,
	resetStudentFinalExam,
	updateFinalExamSettings,
} from "../api/finalExams";
import { formatDate } from "../../utils/date";
import { formatRut } from "../../utils/rutUtils";
import { capitalizeFirstLetter, capitalizeWords} from "../../utils/letters";

function getErrorMessage(error) {
	return error?.response?.data?.detail || error?.response?.data?.error || error?.message || "No fue posible completar la acción.";
}

const SORT_OPTIONS = [
	{ value: "organization_name", label: "Organización" },
	{ value: "course_name", label: "Curso" },
	{ value: "teacher_name", label: "Docente" },
	{ value: "start_date", label: "Fecha inicio" },
	{ value: "end_date", label: "Fecha fin" },
];

function getExamSortValue(exam, field) {
	switch (field) {
		case "organization_name":
			return exam.organization_name || "";
		case "course_name":
			return exam.course_name || "";
		case "teacher_name":
			return exam.teacher_name || "";
		case "start_date":
			return exam.start_date ? new Date(exam.start_date).getTime() : 0;
		case "end_date":
			return exam.end_date ? new Date(exam.end_date).getTime() : 0;
		case "course_code":
		default:
			return exam.course_code || "";
	}
}

function ExamStudentsTable({ students, onReset }) {
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return students;
		return students.filter(
			(s) =>
				(s.student_name || "").toLowerCase().includes(q) ||
				(s.student_rut || "").toLowerCase().includes(q)
		);
	}, [students, search]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
	const safeCurrentPage = Math.min(currentPage, totalPages);
	const startIndex = filtered.length === 0 ? 0 : (safeCurrentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, filtered.length);
	const paginated = filtered.slice(startIndex, endIndex);

	useEffect(() => {
		setCurrentPage(1);
	}, [search]);

	return (
		<div className="mb-4">
			<h5 className="fw-bold mb-2">Estudiantes</h5>
			<div className="card shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1}>&lt;&lt;</button>
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safeCurrentPage === 1}>&lt;</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={safeCurrentPage}
								onChange={(event) => setCurrentPage(Number(event.target.value))}
								disabled={totalPages <= 1}
							>
								{Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safeCurrentPage >= totalPages}>&gt;</button>
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage >= totalPages}>&gt;&gt;</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar estudiante..."
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setSearch("")}
								disabled={!search.trim()}
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(event) => {
										setRowsPerPage(Number(event.target.value));
										setCurrentPage(1);
									}}
								>
									<option value={5}>5</option>
									<option value={10}>10</option>
									<option value={20}>20</option>
									<option value={50}>50</option>
								</select>
							</label>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle">
							<thead className="table-light">
								<tr>
									<th><span>Estudiante</span></th>
									<th><span>RUT</span></th>
									<th><span>Nota</span></th>
									<th><span>Intentos</span></th>
									<th><span>Certificado</span></th>
									<th className="text-end"><span>Acción</span></th>
								</tr>
							</thead>
							<tbody>
								{paginated.length === 0 ? (
									<tr>
										<td colSpan="6" className="text-center text-muted p-4"><span>No hay estudiantes para mostrar.</span></td>
									</tr>
								) : (
									paginated.map((student) => (
										<tr key={student.student_id}>
											<td><span>{capitalizeWords(student.student_name || "-")}</span></td>
											<td><span>{formatRut(student.student_rut) || "-"}</span></td>
											<td><span>{student.final_grade != null ? Number(student.final_grade).toFixed(1) : "-"}</span></td>
											<td><span>{student.valid_attempts_count} válidos / {student.invalidated_attempts_count} anulados</span></td>
											<td><span>{student.certificate?.is_valid ? "Vigente" : student.certificate ? "Invalidado" : "-"}</span></td>
											<td className="text-end">
												<button type="button" className="btn-sm adm-del-btn border-0" onClick={() => onReset(student)}>
													<i className="ri-restart-line" />
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					<div className="table-footer-summary">
						<span>
							Mostrando {filtered.length === 0 ? 0 : startIndex + 1} - {endIndex} de {filtered.length} estudiantes
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}

function ExamLogsTable({ logs }) {
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return logs;
		return logs.filter(
			(l) =>
				(l.performed_by || "").toLowerCase().includes(q) ||
				(l.student || "").toLowerCase().includes(q) ||
				(l.reason || "").toLowerCase().includes(q)
		);
	}, [logs, search]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
	const safeCurrentPage = Math.min(currentPage, totalPages);
	const startIndex = filtered.length === 0 ? 0 : (safeCurrentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, filtered.length);
	const paginated = filtered.slice(startIndex, endIndex);

	useEffect(() => {
		setCurrentPage(1);
	}, [search]);

	return (
		<div>
			<h5 className="fw-bold mb-2">Historial administrativo</h5>
			<div className="card shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1}>&lt;&lt;</button>
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safeCurrentPage === 1}>&lt;</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={safeCurrentPage}
								onChange={(event) => setCurrentPage(Number(event.target.value))}
								disabled={totalPages <= 1}
							>
								{Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safeCurrentPage >= totalPages}>&gt;</button>
							<button type="button" className="toolbar-nav-btn" onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage >= totalPages}>&gt;&gt;</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar historial..."
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setSearch("")}
								disabled={!search.trim()}
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(event) => {
										setRowsPerPage(Number(event.target.value));
										setCurrentPage(1);
									}}
								>
									<option value={5}>5</option>
									<option value={10}>10</option>
									<option value={20}>20</option>
									<option value={50}>50</option>
								</select>
							</label>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle">
							<thead className="table-light">
								<tr>
									<th><span>Fecha</span></th>
									<th><span>Tipo</span></th>
									<th><span>Admin</span></th>
									<th><span>Estudiante</span></th>
									<th><span>Motivo</span></th>
									<th><span>Impacto</span></th>
								</tr>
							</thead>
							<tbody>
								{paginated.length === 0 ? (
									<tr>
										<td colSpan="6" className="text-center text-muted p-4"><span>Sin movimientos registrados.</span></td>
									</tr>
								) : (
									paginated.map((log) => (
										<tr key={log.id}>
											<td><span>{formatDate(log.performed_at)}</span></td>
											<td><span>{log.reset_type}</span></td>
											<td><span>{log.performed_by || "-"}</span></td>
											<td><span>{log.student || "-"}</span></td>
											<td>
												<span title={log.reason || ""}>
													{log.reason && log.reason.length > 15
														? log.reason.substring(0, 15) + "..."
														: log.reason || "-"}
												</span>
											</td>
											<td><span>{log.affected_attempts_count} intentos / {log.invalidated_certificates_count} certificados</span></td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					<div className="table-footer-summary">
						<span>
							Mostrando {filtered.length === 0 ? 0 : startIndex + 1} - {endIndex} de {filtered.length} movimientos
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function FinalExamsAdmin() {
	const [exams, setExams] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [sortField, setSortField] = useState("organization_name");
	const [sortDirection, setSortDirection] = useState("asc");
	const [selectedExam, setSelectedExam] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [settingsForm, setSettingsForm] = useState({
		time_limit_minutes: "",
		max_attempts: 1,
		reason: "",
	});
	const [resetReason, setResetReason] = useState("");
	const [studentReset, setStudentReset] = useState(null);
	const [studentResetReason, setStudentResetReason] = useState("");
	const [savingAction, setSavingAction] = useState(false);

	async function loadExams() {
		setLoading(true);
		setError("");
		try {
			setExams(await listFinalExams());
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setLoading(false);
		}
	}

	async function openDetail(examId) {
		setDetailLoading(true);
		setError("");
		setSuccess("");
		try {
			const data = await getFinalExam(examId);
			setSelectedExam(data);
			setSettingsForm({
				time_limit_minutes: data.time_limit_minutes || "",
				max_attempts: data.max_attempts || 1,
				reason: "",
			});
			setResetReason("");
			setStudentReset(null);
			setStudentResetReason("");
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setDetailLoading(false);
		}
	}

	useEffect(() => {
		loadExams();
	}, []);

	const filteredExams = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return exams;
		return exams.filter((exam) =>
			[
				exam.course_code,
				exam.course_name,
				exam.organization_name,
				exam.teacher_name,
				exam.course_status,
			]
				.join(" ")
				.toLowerCase()
				.includes(q),
		);
	}, [exams, search]);

	const sortedExams = useMemo(() => {
		const data = [...filteredExams];
		const directionFactor = sortDirection === "asc" ? 1 : -1;
		data.sort((left, right) => {
			const leftValue = getExamSortValue(left, sortField);
			const rightValue = getExamSortValue(right, sortField);
			if (typeof leftValue === "number" && typeof rightValue === "number") {
				return (leftValue - rightValue) * directionFactor;
			}
			return leftValue
				.toString()
				.toLowerCase()
				.localeCompare(rightValue.toString().toLowerCase(), "es") * directionFactor;
		});
		return data;
	}, [filteredExams, sortField, sortDirection]);

	const totalPages = Math.max(1, Math.ceil(sortedExams.length / rowsPerPage));
	const safeCurrentPage = Math.min(currentPage, totalPages);
	const startIndex = sortedExams.length === 0 ? 0 : (safeCurrentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, sortedExams.length);
	const paginatedExams = sortedExams.slice(startIndex, endIndex);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection]);

	function resetTableState() {
		setSearch("");
		setCurrentPage(1);
		setRowsPerPage(10);
		setSortField("organization_name");
		setSortDirection("asc");
	}

	async function refreshSelectedExam() {
		if (!selectedExam) return;
		await openDetail(selectedExam.id);
		await loadExams();
	}

	async function handleSaveSettings() {
		setSavingAction(true);
		setError("");
		setSuccess("");
		try {
			await updateFinalExamSettings(selectedExam.id, settingsForm);
			setSuccess("Configuración actualizada.");
			await refreshSelectedExam();
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setSavingAction(false);
		}
	}

	async function handleMassiveReset() {
		setSavingAction(true);
		setError("");
		setSuccess("");
		try {
			await resetFinalExam(selectedExam.id, resetReason);
			setSuccess("Reinicio masivo aplicado.");
			await refreshSelectedExam();
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setSavingAction(false);
		}
	}

	async function handleStudentReset() {
		if (!studentReset) return;
		setSavingAction(true);
		setError("");
		setSuccess("");
		try {
			await resetStudentFinalExam(selectedExam.id, studentReset.student_id, studentResetReason);
			setSuccess("Reinicio individual aplicado.");
			await refreshSelectedExam();
		} catch (err) {
			setError(getErrorMessage(err));
		} finally {
			setSavingAction(false);
		}
	}

	return (
		<div className="container-fluid font-n">
			<div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-4">
				<div>
					<h2 className="m-0">Exámenes finales</h2>
					<p className="text-muted mb-0">Reinicio administrativo de exámenes finales con intentos registrados en cursos abiertos.</p>
				</div>
				<button type="button" className="admin-btn text-nowrap" onClick={loadExams} disabled={loading}>
					<span>{loading ? "Actualizando..." : "Actualizar"}</span>
				</button>
			</div>

			{error && <div className="alert alert-danger">{error}</div>}
			{success && <div className="alert alert-success">{success}</div>}

			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={safeCurrentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
								disabled={safeCurrentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={safeCurrentPage}
								onChange={(event) => setCurrentPage(Number(event.target.value))}
								disabled={totalPages <= 1}
							>
								{Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
								disabled={safeCurrentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={safeCurrentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Buscar por organización, curso o docente..."
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(event) => setRowsPerPage(Number(event.target.value))}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(event) => setSortField(event.target.value)}
								>
									{SORT_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								<span>Reiniciar</span>
							</button>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th><span>Organización</span></th>
									<th><span>Curso</span></th>
									<th><span>Docente</span></th>
									<th><span>Fecha inicio</span></th>
									<th><span>Fecha término</span></th>
									<th className="text-end"><span>Acción</span></th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr><td colSpan="7" className="text-center text-muted p-4">Cargando exámenes...</td></tr>
								) : paginatedExams.length === 0 ? (
									<tr><td colSpan="7" className="text-center text-muted p-4">No hay exámenes finales para mostrar.</td></tr>
								) : paginatedExams.map((exam) => (
									<tr key={exam.id}>
										<td><span>{capitalizeWords(exam.organization_name) || "-"}</span></td>
										<td>
											<strong><span>{exam.course_code || "-"}</span></strong>
											<div className="text-muted small"><span>{capitalizeWords(exam.course_name)}</span></div>
										</td>
										<td><span>{capitalizeWords(exam.teacher_name) || "-"}</span></td>
										<td><span>{formatDate(exam.start_date)}</span></td>
										<td><span>{formatDate(exam.end_date)}</span></td>
										<td className="text-end">
											<button type="button" className="btn-sm adm-upd-btn border-0" onClick={() => openDetail(exam.id)}>
												<i className="ri-settings-3-fill" />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="table-footer-summary">
						<span>
							Mostrando {sortedExams.length === 0 ? 0 : startIndex + 1} - {endIndex} de {sortedExams.length} exámenes
						</span>
					</div>
				</div>
			</div>

			{selectedExam && (
				<Modal
					title={`Examen final - ${selectedExam.course_code || ""}`}
					open={Boolean(selectedExam)}
					onClose={() => setSelectedExam(null)}
					wide
				>
					{detailLoading ? (
						<p className="text-muted">Cargando detalle...</p>
					) : (
						<div className="d-flex flex-column gap-4">
							<div className="row g-3">
								<div className="col-md-4">
									<label className="form-label"><span>Tiempo límite</span></label>
									<input
										type="number"
										className="form-control"
										value={settingsForm.time_limit_minutes}
										onChange={(event) => setSettingsForm({ ...settingsForm, time_limit_minutes: event.target.value })}
										placeholder="Sin límite"
									/>
								</div>
								<div className="col-md-4">
									<label className="form-label"><span>Intentos permitidos</span></label>
									<input
										type="number"
										className="form-control"
										min="1"
										value={settingsForm.max_attempts}
										onChange={(event) => setSettingsForm({ ...settingsForm, max_attempts: event.target.value })}
									/>
								</div>
								<div className="col-md-4">
									<label className="form-label"><span>Estado</span></label>
									<div className="form-control bg-light">
										<span>{selectedExam.is_locked ? "Con intentos registrados" : "Sin intentos registrados"}</span>
									</div>
								</div>
								<div className="col-12">
									<label className="form-label"><span>Motivo del ajuste</span></label>
									<textarea
										className="form-control"
										rows={2}
										value={settingsForm.reason}
										onChange={(event) => setSettingsForm({ ...settingsForm, reason: event.target.value })}
										placeholder={selectedExam.is_locked ? "Obligatorio si ya existen intentos" : "Opcional mientras no existan intentos"}
									/>
								</div>
								<div className="col-12 d-flex justify-content-end">
									<button type="button" className="adm-save-btn" onClick={handleSaveSettings} disabled={savingAction}>
										<i className="ri-save-line me-2" />
										<span>Guardar configuración</span>
									</button>
								</div>
							</div>

							<div className="border-top pt-3">
								<h5 className="fw-bold">Reinicio masivo</h5>
								<textarea
									className="form-control mb-2"
									rows={2}
									value={resetReason}
									onChange={(event) => setResetReason(event.target.value)}
									placeholder="Motivo obligatorio del reinicio masivo"
								/>
								<button type="button" className="adm-del-btn" onClick={handleMassiveReset} disabled={savingAction || !resetReason.trim()}>
									<i className="ri-restart-line me-2" />
									<span>Reiniciar examen completo</span>
								</button>
							</div>

							<ExamStudentsTable students={selectedExam.students || []} onReset={setStudentReset} />
							<ExamLogsTable logs={selectedExam.logs || []} />
						</div>
					)}
				</Modal>
			)}

			{studentReset && (
				<Modal
					title={`Reiniciar examen - ${capitalizeWords(studentReset.student_name)}`}
					open={Boolean(studentReset)}
					onClose={() => setStudentReset(null)}
				>
					<label className="form-label"><span>Motivo del reinicio individual</span></label>
					<textarea
						className="form-control mb-3"
						rows={3}
						value={studentResetReason}
						onChange={(event) => setStudentResetReason(event.target.value)}
					/>
					<div className="d-flex justify-content-end gap-2">
						<button type="button" className="adm-del-btn" onClick={() => setStudentReset(null)}>
							<span>Cancelar</span>
						</button>
						<button type="button" className="adm-save-btn" onClick={handleStudentReset} disabled={savingAction || !studentResetReason.trim()}>
							<span>Confirmar reinicio</span>
						</button>
					</div>
				</Modal>
			)}
		</div>
	);
}
